import { useEffect, useState } from "react";
import { useSession } from "./hooks";
import type { Principal } from "./sync/types";

// bigDirectory is how many people a client searches by itself; past it,
// the server finds them, as a scan of tens of thousands on every keystroke
// makes typing lag.
export const bigDirectory = 2000;

// searchDelay is how long typing rests before the server is asked.
const searchDelay = 120;

// usePeopleSearch is who the server finds for what is typed, in a large
// workspace; null in a small one, where the client searches its directory.
// While an answer is coming, the last one stays.
export function usePeopleSearch(query: string | null): Principal[] | null {
  const session = useSession();
  const big = session.directory.size > bigDirectory;
  const [found, setFound] = useState<Principal[]>([]);
  const q = query?.trim() ?? "";
  useEffect(() => {
    if (!big || !q) return setFound([]);
    let live = true;
    const timer = setTimeout(() => {
      session.api.principals({ q, limit: 12 }).then(
        (r) => {
          // So whoever is picked has a name everywhere after.
          for (const p of r.principals) if (!session.directory.has(p.id)) session.directory.set(p.id, p);
          if (live) setFound(r.principals);
        },
        () => {},
      );
    }, searchDelay);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [big, q, session]);
  return big && query !== null ? found : null;
}
